/**
 * Message Centre — every automated email + SMS in one list, grouped by area,
 * each with a plain-English "when it's sent" note and an Edit action.
 * Emails open the existing editors; automated SMS bodies edit inline here.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Home, Mail, MessageSquare, Pencil, Send, Loader2, RotateCcw, Clock, Zap, Hand, FileText, Search, History, ChevronUp, CalendarClock, PoundSterling, Users } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatApiError } from '@/utils/apiError';
import { MessageFieldsPanel } from '@/components/staff/MessageFieldsPanel';
import { TriggerGlyph } from '@/components/messages/TriggerGlyph';

const CATEGORY_ORDER = ['Training Courses', 'Customer Leads', 'Ox Approved Programme', 'Marketing & Upsells', 'Project Scheduling', 'Orders & Quotes', 'Stock & Suppliers', 'Ambassador Referrals', 'Accounts & Logins'];
const FILTERS = [{ key: 'all', label: 'All' }, { key: 'email', label: 'Emails' }, { key: 'sms', label: 'Texts' }];
const TRIGGERS = [
  ['all', 'Any trigger', null], ['automatic', 'Automatic', Zap], ['staff', 'Staff button', Hand], ['installer', 'Installer button', Hand], ['request', 'Form / request', FileText],
];
const AUDIENCES = [['all', 'Anyone'], ['trainee', 'Trainees'], ['prospect', 'Enquiries'], ['installer', 'Installers'], ['customer', 'Customers'], ['staff', 'Staff (internal)']];
const AUDIENCE_LABEL = { trainee: 'Trainee', prospect: 'Enquiry', installer: 'Installer', customer: 'Customer', staff: 'Staff' };

export default function MessageCentre() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [trigger, setTrigger] = useState('all');
  const [audience, setAudience] = useState('all');
  const [query, setQuery] = useState('');
  const [openKey, setOpenKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/message-centre`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load');
      setItems((await res.json()).items || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isStaff) load(); }, [isStaff, load]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = items.filter((i) => (filter === 'all' || i.channel === filter) && (trigger === 'all' || i.trigger === trigger) && (audience === 'all' || i.audience === audience)
      && (!q || `${i.label} ${i.description} ${i.category} ${i.timing || ''}`.toLowerCase().includes(q)));
    const cats = [...new Set(list.map((i) => i.category))].sort((a, b) => (CATEGORY_ORDER.indexOf(a) + 99) % 99 - (CATEGORY_ORDER.indexOf(b) + 99) % 99);
    return cats.map((c) => ({ category: c, rows: list.filter((i) => i.category === c).sort((a, b) => (a.channel === b.channel ? 0 : a.channel === 'email' ? -1 : 1)) }));
  }, [items, filter, trigger, audience, query]);

  const counts = useMemo(() => ({ all: items.length, email: items.filter((i) => i.channel === 'email').length, sms: items.filter((i) => i.channel === 'sms').length }), [items]);

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex flex-wrap items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} data-testid="message-centre-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 min-w-0">
              <Send className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> <span className="truncate"><span className="sm:hidden">Messages</span><span className="hidden sm:inline">Email &amp; Message Centre</span></span>
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Every email and text the portal sends, the details merged into them, and where to edit each one — all in one place.</p>
          </div>
          <div className="w-full flex flex-wrap items-center gap-2" data-testid="message-centre-nav">
          <Button variant="outline" onClick={() => navigate('/staff/email-templates')} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="message-centre-email-templates" title="Build and send your own email templates">
            <Mail className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Email templates</span>
          </Button>
          <Button variant="outline" onClick={() => navigate('/staff/message-centre/activity')} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="message-centre-activity" title="Every email, text and push that actually went out">
            <History className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Sent log</span>
          </Button>
          <Button variant="outline" onClick={() => navigate('/staff/message-centre/text-spend')} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="message-centre-text-spend" title="How many texts went out each month and what they cost">
            <PoundSterling className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Text spend</span>
          </Button>
          <Button variant="outline" onClick={() => navigate('/staff/message-centre/schedules')} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="message-centre-schedules" title="When each automated text, email and push goes out">
            <CalendarClock className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Automated messages</span>
          </Button>
          <Button onClick={() => navigate('/staff/message-centre/people')} className="bg-violet-600 hover:bg-violet-500 text-white border-0" data-testid="message-centre-people" title="Pick a team member and tick which texts, emails and push notifications they receive">
            <Users className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Who receives what</span>
          </Button>
          <Button variant="outline" onClick={() => navigate('/staff/sms-templates')} className="bg-white/10 border-white/30 text-white hover:bg-white/20 hidden sm:inline-flex" data-testid="message-centre-snippets" title="Reusable copy-and-paste text snippets for staff">
            <MessageSquare className="w-4 h-4 mr-1.5" /> Staff snippets
          </Button>
          <Button variant="outline" onClick={() => navigate('/staff/email-samples')} className="bg-white/10 border-white/30 text-white hover:bg-white/20 hidden sm:inline-flex" data-testid="message-centre-email-editor" title="Full email editor, preview and send-test">
            <Mail className="w-4 h-4 mr-1.5" /> Email editor
          </Button>
          <Button variant="outline" onClick={() => navigate('/staff')} className="bg-white/10 border-white/30 text-white hover:bg-white/20 hidden sm:inline-flex" data-testid="message-centre-home">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-5 space-y-5">
        <MessageFieldsPanel />
        <div className="flex flex-wrap items-center gap-2" data-testid="message-centre-filters">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} data-testid={`message-filter-${f.key}`}
              className={`px-3 py-2 rounded-lg text-sm font-bold border-2 transition-colors ${filter === f.key ? 'bg-[#0B0B0E] text-white border-transparent shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>
              {f.label}<span className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${filter === f.key ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{counts[f.key]}</span>
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search messages… e.g. balance" className="pl-9 h-10 bg-white" data-testid="message-centre-search" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="message-centre-trigger-filters">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mr-1">Sent by</span>
          {TRIGGERS.map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setTrigger(k)} data-testid={`message-trigger-${k}`} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${trigger === k ? 'bg-violet-700 text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>{Icon && <Icon className="w-3.5 h-3.5" />}{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="message-centre-audience-filters">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mr-1">Sent to</span>
          {AUDIENCES.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setAudience(k)} data-testid={`message-audience-${k}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${audience === k ? 'bg-[#1f4030] text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-gray-500" data-testid="message-centre-legend">
          <span className="inline-flex items-center gap-1"><span className="inline-flex w-5 h-5 rounded bg-violet-100 text-violet-800 items-center justify-center"><Zap className="w-3 h-3" /></span> Automatic</span>
          <span className="inline-flex items-center gap-1"><span className="inline-flex w-5 h-5 rounded bg-amber-100 text-amber-800 items-center justify-center"><Hand className="w-3 h-3" /></span> Button press</span>
          <span className="inline-flex items-center gap-1"><span className="inline-flex w-5 h-5 rounded bg-slate-200 text-slate-700 items-center justify-center"><FileText className="w-3 h-3" /></span> Form</span>
          <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-emerald-700" /> Email · <MessageSquare className="w-3.5 h-3.5 text-sky-700" /> Text</span>
          <span className="inline-flex items-center gap-1"><ArrowRight className="w-3 h-3" /> what fires it → the message · <ArrowLeft className="w-3 h-3" /> sent <em>before</em> a date</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Live = edits change what's sent</span>
        </div>

        {loading ? <Card className="p-10 text-center text-gray-500">Loading…</Card> : grouped.length === 0 ? <Card className="p-10 text-center text-gray-500" data-testid="message-centre-empty">No messages match those filters.</Card> : grouped.map((g) => (
          <section key={g.category} data-testid={`message-group-${g.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 px-1">{g.category}</h2>
            <Card className="divide-y divide-gray-100 overflow-hidden">
              {g.rows.map((row) => <MessageRow key={row.key} row={row} open={openKey === row.key} onToggle={() => setOpenKey((k) => (k === row.key ? null : row.key))} onSaved={load} />)}
            </Card>
          </section>
        ))}
      </div>

    </div>
  );
}

function SmsSwitch({ row, onSaved }) {
  const [busy, setBusy] = useState(false);
  const on = !row.sms_disabled;
  const flip = async (e) => {
    e.stopPropagation(); setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/message-centre/sms/${row.key}/enabled`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !on }) });
      if (!res.ok) throw new Error(await formatApiError(res));
      toast.success(!on ? `Text switched on: ${row.label}` : `Text switched off: ${row.label} — the email still goes`);
      onSaved?.();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <button type="button" role="switch" aria-checked={on} onClick={flip} disabled={busy} title={on ? 'Text is ON — click to switch off (email only)' : 'Text is OFF — click to switch on'}
      className={`inline-flex items-center gap-1.5 shrink-0 rounded-full pl-1 pr-2 py-0.5 border text-[10px] font-bold uppercase tracking-wider transition-colors ${on ? 'bg-sky-600 border-sky-600 text-white' : 'bg-gray-100 border-gray-200 text-gray-600'}`}
      data-testid={`sms-switch-${row.key}`} data-on={on}>
      <span className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${on ? '' : 'opacity-60'}`} />{on ? 'Text on' : 'Text off'}
    </button>
  );
}

const ago = (iso) => {
  const d = new Date(iso); const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} hr ago`;
  const days = Math.round(mins / 1440);
  if (days < 14) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
};

function SendHistory({ history, live, testId }) {
  if (!live) return null;
  if (!history || !history.last_sent_at) {
    return <p className="mt-1.5 text-[11px] text-gray-400 inline-flex items-center gap-1" data-testid={testId}><History className="w-3 h-3" /> Not sent yet</p>;
  }
  const { last_sent_at, sent_30d, recipients_30d, sent_total } = history;
  const people = recipients_30d === 1 ? '1 person' : `${recipients_30d} people`;
  return (
    <p className="mt-1.5 text-[11px] text-gray-600 inline-flex flex-wrap items-center gap-x-1.5" data-testid={testId}>
      <History className="w-3 h-3 text-gray-400" />
      <span>Last sent <strong className="text-gray-800">{ago(last_sent_at)}</strong></span>
      <span className="text-gray-300">·</span>
      {sent_30d > 0 ? <span><strong className="text-gray-800">{sent_30d}</strong> sent to <strong className="text-gray-800">{people}</strong> in the last 30 days</span> : <span>none in the last 30 days</span>}
      <span className="text-gray-300">·</span>
      <span>{sent_total} all time</span>
    </p>
  );
}

function TestMeButton({ row }) {
  const [busy, setBusy] = useState(false);
  const isSms = row.channel === 'sms';
  const go = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/message-centre/${row.key}/test-me`, { method: 'POST', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(d) || 'Could not send');
      toast.success(`${isSms ? 'Test text' : 'Test email'} sent to ${d.to}${d.status && d.status !== 'sent' ? ` · ${d.status}` : ''}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={go} disabled={busy} className={`h-8 w-full sm:w-auto ${isSms ? 'border-sky-300 text-sky-800 hover:bg-sky-50' : 'border-emerald-300 text-emerald-800 hover:bg-emerald-50'}`} title={isSms ? 'Text this to my mobile now' : 'Email this to me now'} data-testid={`message-test-${row.key}`}>
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} {isSms ? 'Text me a test' : 'Email me a test'}
    </Button>
  );
}

function MessageRow({ row, open, onToggle, onSaved }) {
  const navigate = useNavigate();
  const isSms = row.channel === 'sms';
  const externalOnly = !isSms && row.edit_href && !row.edit_href.startsWith('/staff/email-samples');
  return (
    <div className={open ? 'bg-gray-50/60' : ''} data-testid={`message-row-${row.key}`}>
    <div className="px-3 sm:px-4 py-3 flex items-start gap-3">
      <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSms ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`} title={isSms ? 'Text message' : 'Email'}>
        {isSms ? <MessageSquare className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">{row.label}</h3>
          <div className="flex items-center gap-1 shrink-0">
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold ${row.live ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid={`message-live-${row.key}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${row.live ? 'bg-white' : 'bg-gray-400'}`} />{row.live ? 'Live' : 'Preview'}
            </span>
            {row.is_overridden && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold bg-amber-100 text-amber-800" data-testid={`message-edited-${row.key}`}>Edited</span>}
            {isSms && <SmsSwitch row={row} onSaved={onSaved} />}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`message-meta-${row.key}`}>
          <TriggerGlyph row={row} />
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">→ {AUDIENCE_LABEL[row.audience] || row.audience}</span>
        </div>
        {isSms && row.sms_disabled && <p className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid={`message-sms-off-${row.key}`}>Text switched off — recipients get the email only.</p>}
        {row.timing && <p className="mt-1.5 text-xs text-gray-600 inline-flex items-start gap-1" data-testid={`message-when-${row.key}`}><Clock className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" /> <span>{row.timing}</span></p>}
        <p className="mt-1 text-xs sm:text-sm text-gray-600 hidden sm:block">{row.description}</p>
        <SendHistory history={row.history} live={row.live} testId={`message-history-${row.key}`} />
        <div className="mt-2.5 flex flex-col sm:flex-row gap-2">
          <Button size="sm" variant={open ? 'default' : 'outline'} onClick={() => (externalOnly ? navigate(row.edit_href) : onToggle())} data-testid={`message-edit-${row.key}`} className={`h-8 w-full sm:w-auto ${open ? 'bg-[#1f4030] hover:bg-[#16302a] text-white' : ''}`}>
            {open ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <Pencil className="w-3.5 h-3.5 mr-1" />} {open ? 'Close' : row.editable ? 'View & edit' : 'View'}
          </Button>
          <TestMeButton row={row} />
        </div>
      </div>
    </div>
    {open && (
      <div className="px-3 sm:px-4 pb-4" data-testid={`message-editor-${row.key}`}>
        {row.system ? <SystemViewer msgKey={row.key} /> : isSms ? <SmsEditor smsKey={row.key} onClose={onToggle} onSaved={onSaved} /> : <EmailEditor emailKey={row.key} onClose={onToggle} onSaved={onSaved} />}
      </div>
    )}
    </div>
  );
}

function SystemViewer({ msgKey }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/staff/message-centre/system/${msgKey}`, { credentials: 'include' }).then((r) => r.json()).then(setD).catch(() => toast.error('Could not load message'));
  }, [msgKey]);
  if (!d) return <div className="py-6 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
  return (
    <div className="space-y-3" data-testid={`system-viewer-${msgKey}`}>
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">This message is built from live data when it's sent, so the wording below is a representative example rather than an editable template.</p>
      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        <div className="bg-white border rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Who receives it</div><div className="text-gray-800" data-testid={`system-recipients-${msgKey}`}>{d.recipients}</div></div>
        <div className="bg-white border rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">When it's sent</div><div className="text-gray-800">{d.timing}</div></div>
      </div>
      <div className="bg-white border rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Example</div>
        {d.sample_subject && <div className="text-sm font-semibold text-gray-900 mb-1" data-testid={`system-sample-subject-${msgKey}`}>{d.sample_subject}</div>}
        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-testid={`system-sample-body-${msgKey}`}>{d.sample_body}</pre>
      </div>
      <div className="bg-white border rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Last sent</div>
        {!d.recent?.length ? <div className="text-xs text-gray-400 italic">Not sent yet</div> : (
          <ul className="divide-y text-xs" data-testid={`system-recent-${msgKey}`}>
            {d.recent.map((r, i) => <li key={i} className="py-1.5 flex gap-3"><span className="text-gray-500 whitespace-nowrap">{fmt(r.at)}</span><span className="text-gray-700 truncate max-w-[200px]">{r.to}</span><span className="flex-1 text-gray-800 truncate" title={r.preview}>{r.preview}</span><span className={r.ok ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'} title={r.error || ''}>{r.ok ? 'Sent' : 'Failed'}</span></li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

function EmailEditor({ emailKey, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState({ subject: '', html: '' });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [showCode, setShowCode] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/staff/email-samples/${emailKey}/source`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setData(d); setSubject(d.subject_source); setBody(d.body_source); setPreview({ subject: d.subject_rendered, html: d.body_rendered }); })
      .catch(() => toast.error('Could not load email'));
  }, [emailKey]);

  useEffect(() => {
    if (!data) return undefined;
    if (subject === data.subject_source && body === data.body_source) { setPreview({ subject: data.subject_rendered, html: data.body_rendered }); return undefined; }
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/staff/email-samples/${emailKey}/render`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ subject, body }) })
        .then((r) => r.json()).then((d) => setPreview({ subject: d.subject_rendered || '', html: d.body_rendered || '' })).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [subject, body, data, emailKey]);

  const call = async (method, url, payload, okMsg) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api${url}`, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: payload ? JSON.stringify(payload) : undefined });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Failed');
      toast.success(okMsg);
      return d;
    } catch (e) { toast.error(e.message); return null; } finally { setBusy(false); }
  };
  const reload = async () => {
    const r = await fetch(`${API_URL}/api/staff/email-samples/${emailKey}/source`, { credentials: 'include' });
    const d = await r.json(); setData(d); setSubject(d.subject_source); setBody(d.body_source); onSaved();
  };
  const save = async () => { if (await call('POST', `/staff/email-samples/${emailKey}/publish`, { subject, body }, 'Saved — new sends use this wording')) await reload(); };
  const revert = async () => { if (await call('DELETE', `/staff/email-samples/${emailKey}/override`, null, 'Reverted to default')) await reload(); };
  const sendTest = async () => { if (testTo.trim()) await call('POST', '/staff/email-samples/send', { template_key: emailKey, to: testTo.trim(), subject_override: subject, body_override: body }, `Test sent to ${testTo.trim()}`); };
  const dirty = data && (subject !== data.subject_source || body !== data.body_source);

  if (!data) return <p className="text-sm text-gray-500 py-2">Loading…</p>;
  return (
    <div className="rounded-xl border bg-white p-3 sm:p-4 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="email-editor">
      <div className="space-y-2 min-w-0">
        {!data.editable && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">{data.dynamic_note || 'This email is built from live data, so only the preview is shown here.'}</p>}
        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500">Subject
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!data.editable} className="mt-1 h-9 bg-white font-normal normal-case tracking-normal" data-testid="email-editor-subject" />
        </label>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Body</span>
          <button type="button" onClick={() => setShowCode((v) => !v)} className="text-[11px] text-sky-700 hover:underline" data-testid="email-editor-toggle-code">{showCode ? 'Hide HTML' : 'Edit wording (HTML)'}</button>
        </div>
        {showCode ? (
          <Textarea rows={16} value={body} onChange={(e) => setBody(e.target.value)} disabled={!data.editable} className="font-mono text-xs bg-white" data-testid="email-editor-body" />
        ) : (
          <p className="text-xs text-gray-500 border border-dashed rounded-lg px-3 py-2">The preview on the right is exactly what's sent. Click <strong>Edit wording</strong> to change the text — merge fields like <code className="font-mono">{'{{first_name}}'}</code> fill in automatically.</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {(data.merge_fields || []).map((p) => (
            <button key={p} type="button" onClick={() => { setShowCode(true); setBody((b) => `${b}{{${p}}}`); }} className="text-[11px] font-mono px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 hover:border-sky-300 hover:bg-sky-50" data-testid={`email-placeholder-${p}`}>{`{{${p}}}`}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {data.editable && <Button size="sm" onClick={save} disabled={busy || !dirty} className="bg-[#1f4030] hover:bg-[#16302a] text-white" data-testid="email-editor-save">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save</Button>}
          {data.is_overridden && <Button size="sm" variant="outline" onClick={revert} disabled={busy} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="email-editor-revert"><RotateCcw className="w-4 h-4 mr-1" /> Revert to default</Button>}
          <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
        </div>
      </div>
      <div className="space-y-2 min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Preview — sample data</p>
        <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900" data-testid="email-editor-preview-subject">{preview.subject}</div>
        <div className="w-full max-h-[460px] overflow-y-auto rounded-lg border bg-white p-3 text-sm" data-testid="email-editor-preview" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html, { ADD_ATTR: ['target'] }) }} />
        <div className="flex gap-2">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@oxresin.co.uk" className="h-9" data-testid="email-editor-test-to" />
          <Button variant="outline" onClick={sendTest} disabled={busy || !testTo.trim()} className="h-9 shrink-0" data-testid="email-editor-send-test"><Send className="w-4 h-4 mr-1" /> Send test</Button>
        </div>
      </div>
    </div>
  );
}

const GSM_OK = /^[\u0000-\u007f£€¥§¿¡ÄÖÑÜäöñüàèéìòùßÅåø]*$/;
const segments = (t) => { const s = t || ''; const ucs = !GSM_OK.test(s); const single = ucs ? 70 : 160; const multi = ucs ? 67 : 153; return s.length <= single ? 1 : Math.ceil(s.length / multi); };

function SmsEditor({ smsKey, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/staff/message-centre/sms/${smsKey}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setData(d); setBody(d.body); setPreview(d.preview); })
      .catch(() => toast.error('Could not load template'));
  }, [smsKey]);

  useEffect(() => {
    if (!data || body === data.body) { if (data) setPreview(data.preview); return; }
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/staff/message-centre/sms/${smsKey}/preview`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }),
      }).then((r) => r.json()).then((d) => setPreview(d.preview || '')).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [body, data, smsKey]);

  const call = async (method, path, payload, okMsg) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/message-centre/sms/${smsKey}${path}`, {
        method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: payload ? JSON.stringify(payload) : undefined,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Failed');
      toast.success(okMsg);
      return d;
    } catch (e) { toast.error(e.message); return null; } finally { setBusy(false); }
  };

  const save = async () => { const d = await call('PUT', '', { body }, 'Saved — new sends use this wording'); if (d) { setData(d); setBody(d.body); onSaved(); } };
  const revert = async () => { const d = await call('DELETE', '/override', null, 'Reverted to default'); if (d) { setData(d); setBody(d.body); onSaved(); } };
  const sendTest = async () => { if (testTo.trim()) await call('POST', '/send-test', { to: testTo.trim(), body }, `Test sent to ${testTo.trim()}`); };

  return (
    <div className="rounded-xl border bg-white p-3 sm:p-4 space-y-3" data-testid="sms-editor-dialog">
        {!data ? <p className="text-sm text-gray-500">Loading…</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-xs text-gray-600 flex items-start gap-1.5"><Clock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" /><span><span className="font-semibold text-gray-700">When it's sent:</span> {data.description}</span></p>
              <Textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-sm bg-white" data-testid="sms-editor-body" />
              <div className="flex flex-wrap gap-1.5">
                {data.placeholders.map((p) => (
                  <button key={p} type="button" onClick={() => setBody((b) => `${b}{{${p}}}`)} className="text-[11px] font-mono px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 hover:border-sky-300 hover:bg-sky-50" data-testid={`sms-placeholder-${p}`}>{`{{${p}}}`}</button>
                ))}
              </div>
              <p className="text-[11px] text-gray-500">Blank placeholders (e.g. no venue set) drop their line automatically.</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Preview — sample trainee "Sam", your real venue address</p>
              {data.venue_is_set === false && data.placeholders.includes('venue_address') && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="sms-editor-venue-warning">No venue address set yet — texts will say "we'll confirm the full address". Set it under Training → Fees & Settings.</p>
              )}
              <div className="rounded-2xl bg-sky-600 text-white text-sm px-4 py-3 whitespace-pre-wrap shadow-inner" data-testid="sms-editor-preview">{preview}</div>
              <p className="text-[11px] text-gray-500" data-testid="sms-editor-length">{preview.length} characters · ~{segments(preview)} SMS segment{segments(preview) === 1 ? '' : 's'}</p>
              <div className="flex gap-2 pt-2">
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="07700 900123" className="h-9" data-testid="sms-editor-test-to" />
                <Button variant="outline" onClick={sendTest} disabled={busy || !testTo.trim()} className="h-9 shrink-0" data-testid="sms-editor-send-test"><Send className="w-4 h-4 mr-1" /> Send test</Button>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm" onClick={save} disabled={busy || !data || body.trim() === data.body.trim() || body.trim().length < 10} className="bg-[#1f4030] hover:bg-[#16302a] text-white" data-testid="sms-editor-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
          </Button>
          {data?.is_overridden && (
            <Button size="sm" variant="outline" onClick={revert} disabled={busy} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="sms-editor-revert"><RotateCcw className="w-4 h-4 mr-1" /> Revert to default</Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
        </div>
    </div>
  );
}
